
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '@/components/layout/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useAuth, AuthProvider } from '@/contexts/AuthContext';
import { 
  OverviewTab, 
  PersonalityTab, 
  HealthTab, 
  RecommendationsTab 
} from '@/components/results/TabContent';
import AccountSection from '@/components/results/AccountSection';
import { toast } from '@/hooks/use-toast';
import { QuizProvider, useQuiz } from '@/contexts/QuizContext';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const ResultsContent: React.FC = () => {
  const navigate = useNavigate();
  const { personalityResult, resetQuiz, answers } = useQuiz();
  const { user } = useAuth();
  const [hasResults, setHasResults] = useState(false);
  const [loadingResults, setLoadingResults] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    // Check if user has completed the quiz
    const hasCompletedQuiz = localStorage.getItem('hasCompletedQuiz');
    
    // Check for results in memory or localStorage
    if (personalityResult) {
      console.log("Found results in memory:", personalityResult);
      setHasResults(true);
      setLoadingResults(false);
    } else if (answers && answers.length > 0) {
      console.log("Found answers in memory:", answers);
      setHasResults(true);
      setLoadingResults(false);
    } else {
      // Try to load results from localStorage as a fallback
      try {
        const savedResults = localStorage.getItem('quizResults');
        if (savedResults) {
          const parsedResults = JSON.parse(savedResults);
          console.log("Found results in localStorage:", parsedResults);
          setHasResults(true);
        } else if (!hasCompletedQuiz) {
          // If no results and no completed quiz, set error
          setError("You haven't taken the assessment yet.");
        } else {
          // If quiz was completed but no results, something went wrong
          setError("Unable to load your results. Please try taking the assessment again.");
        }
      } catch (e) {
        console.error("Error parsing saved quiz results:", e);
        setError("Error loading your results. Please try taking the assessment again.");
      }
      setLoadingResults(false);
    }

    // Mark that we've viewed the results
    localStorage.setItem('hasViewedResults', 'true');
  }, [personalityResult, answers]);

  // Redirect to quiz if no results found
  useEffect(() => {
    if (!loadingResults && !hasResults && error) {
      toast({
        title: "No results found",
        description: error || "Please complete the assessment to see your results.",
        variant: "destructive",
        className: "top-center",
      });
      
      // Give the user time to read the toast before redirecting
      const timer = setTimeout(() => {
        navigate('/quiz');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [hasResults, navigate, loadingResults, error]);
  
  // Generate radar data based on personality result
  const generateRadarData = (result: any) => {
    if (!result) return [];
    
    return [
      { subject: 'Physical', value: Math.min(10, Math.round(result.health.score * 2)), fullMark: 10 },
      { subject: 'Environmental', value: Math.min(10, Math.round(result.sdoh.score * 2)), fullMark: 10 },
      { subject: 'Financial', value: Math.min(10, Math.round(result.sdoh.score * 1.5)), fullMark: 10 },
      { subject: 'Social', value: Math.min(10, Math.round(result.relationships.score * 2)), fullMark: 10 },
      { subject: 'Occupational', value: Math.min(10, Math.round(result.personality.score * 2)), fullMark: 10 },
      { subject: 'Mental', value: Math.min(10, Math.round((6 - result.fears.score) * 1.5)), fullMark: 10 },
      { subject: 'Spiritual', value: Math.min(10, Math.round(result.personality.score + result.relationships.score)), fullMark: 10 },
    ];
  };

  const handleRestartQuiz = () => {
    resetQuiz();
    navigate('/quiz');
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  if (loadingResults) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold mb-4">Loading your results...</h2>
        <div className="w-10 h-10 border-4 border-morgenBlue border-t-transparent rounded-full animate-spin mx-auto"></div>
      </div>
    );
  }

  if (!personalityResult && !hasResults) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <Alert variant="destructive" className="max-w-md mx-auto mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error || "No results found. Please complete the assessment to see your results."}
          </AlertDescription>
        </Alert>
        <Button 
          onClick={() => navigate('/quiz')} 
          className="bg-morgen-gradient text-white hover:opacity-90 transition-opacity"
        >
          Take the Assessment
        </Button>
      </div>
    );
  }

  // Use either the in-memory results or get from localStorage
  const result = personalityResult || JSON.parse(localStorage.getItem('quizResults') || '{}');
  const radarData = generateRadarData(result);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-10"
      >
        <h1 className="text-3xl md:text-4xl font-bold mb-4">Your Digital Twin Results</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Based on your responses, we've created a comprehensive profile of your health and wellbeing.
        </p>
      </motion.div>
      
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full mb-12">
        <TabsList className="mx-auto mb-8 flex flex-wrap justify-center gap-1 md:gap-2">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="personality">Personality</TabsTrigger>
          <TabsTrigger value="health">Health</TabsTrigger>
          <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview">
          <OverviewTab radarData={radarData} />
        </TabsContent>
        
        <TabsContent value="personality">
          <PersonalityTab personalityResult={result} />
        </TabsContent>
        
        <TabsContent value="health">
          <HealthTab personalityResult={result} />
        </TabsContent>
        
        <TabsContent value="recommendations">
          <RecommendationsTab personalityResult={result} />
        </TabsContent>
      </Tabs>
      
      <AccountSection 
        user={user} 
        resetQuiz={resetQuiz} 
        handleRestartQuiz={handleRestartQuiz} 
      />
    </div>
  );
};

const Results: React.FC = () => {
  return (
    <Layout>
      <AuthProvider>
        <QuizProvider>
          <ResultsContent />
        </QuizProvider>
      </AuthProvider>
    </Layout>
  );
};

export default Results;
